import React from "react"
import {Box, CardContent, Chip} from "@mui/material";
import CCORichText from "../../fundamentals/CCORichText/CCORichText";
import CCORoofline from "../../fundamentals/CCORoofline";
import CCOLinkText, {type} from "../../fundamentals/CCOLinkText";
import {useTheme} from "@mui/material/styles";
import CCOPicture from "../../fundamentals/CCOPicture";
import {CmTaxonomy, SosDonationProduct} from "../../../../packages/graphql-layer/src";
import FavoriteBorderOutlinedIcon from '@mui/icons-material/FavoriteBorderOutlined';
import {DESCRIPTION_CHAR_LIMIT, ROOF_LINE_CHAR_LIMIT} from "../C020TeaserCarousel/TeaserCardContent";
import {useTranslation} from "react-i18next";
import {TFunction} from "i18next";
import useWindowSize from "../../../utility/useWindowSize";
import CCOHeadlineAsText from "../../fundamentals/CCOHeadlineAsText";


interface Props {
    item: SosDonationProduct,
    horizontal?: boolean,
    headlineType?: string,
    descriptionCharLimit?: number,
    callToActionCustomText?: string,
    suppressRoofline?: boolean,
    isPageView?: boolean,
    suppressCTA?: boolean
}

const cardTextDecorationStyle = {
    textDecoration: 'none',
    color: 'rgb(77, 132, 31)',
    textDecorationThickness: "2px",
    textUnderlineOffset: "4px",
}

export const productCardStyle = (props: Props, color: string) => (
    {
        display: 'grid',
        gridTemplateColumns: props.horizontal ? '1fr 1fr' : 'unset',
        gridTemplateRows: props.horizontal ? 'unset' : 'max-content max-content',
        height: '100%!important',
        width: '100%!important',
        '&:hover': {
            '& p': {
                color: !props.item.teaserTarget ? 'inherit' : color,
                transition: !props.item.teaserTarget ? 'unset' : 'color 1s ease-out',
            },
            '& div > p': !props.item.teaserTarget ? 'unset' : cardTextDecorationStyle,
        },
    });

export function renderProductPicture(product: SosDonationProduct,
                                     t: TFunction,
                                     isHorizontal: boolean,
                                     isWindowExtraLarge: boolean) {
    function getIcon() {
        return product?.new ?
            t("c085_flag_new_product") :
            product?.recommendation ?
                t("c085_flag_recommendation_product") :
                undefined;
    }

    const icon = getIcon();
    const boxSx = {"img": {
        width: '100%!important',
            height: '100%'
        },
        position: 'relative',
        display: 'inline-block'
    };
    const chipSx = {
        marginTop: '-42px',
        position: 'absolute',
        zIndex: 500,
        borderRadius: '5px 0 0 5px',
        right: 0,
        left: 'auto',
        backgroundColor: '#FE3908'
    };
    return product?.picture &&
        <Box overflow='hidden'
             sx={boxSx}>
            {isHorizontal && !(isWindowExtraLarge) ? renderHorizontalPicture(product) : renderNormalPicture(product)}
            {icon ? <Chip color='error' variant={'filled'} icon={<FavoriteBorderOutlinedIcon />} label={icon} sx={chipSx}/> : null}
        </Box>;
}

function formatPrice(price: string, t: TFunction) {
    return price ? price + ' ' + t('cc0_euro') : '';
}

export function renderPrice(price: string|undefined, t: TFunction, priceStyle: any) {
    return price ? <CCOHeadlineAsText textAlign='left'
                                      title={formatPrice(price, t)}
                                      variant='h3'
                                      sx={priceStyle}/> : null;
}

function renderNormalPicture(product: SosDonationProduct) {
    return <CCOPicture name={product?.picture?.name}
                       picture={product?.picture}
                       orientation='landscape_ratio16x9' width='585'
                       height='329' />
}

function renderHorizontalPicture(product: SosDonationProduct) {
    return <CCOPicture name={product?.picture?.name}
                       picture={product?.picture}
                       orientation='landscape_ratio4x3' width='768'
                       height='576' />
}

function renderContent(props: Props, t: TFunction, isWindowExtraLarge: boolean, priceStyle: any) {
    return <React.Fragment>
        {
            renderProductPicture(props.item, t, !!props.horizontal, isWindowExtraLarge)
        }
        {
            renderText(props, t, priceStyle)
        }
    </React.Fragment>
}

function renderText(props: Props, t: TFunction, priceStyle: any) {
    const CARD_CONTENT_STYLE = {
        'div:last-child': {
            marginBottom: '0 !important',
        },
        'span > div:last-child': {
            marginBottom: '24px !important',
        },
        paddingLeft: '24px',
        paddingTop: '24px',
        height: '100%!important',
        width: '100%!important',
        backgroundColor: 'white',
    }

    return <CardContent tabIndex={undefined} sx={CARD_CONTENT_STYLE}>
        {renderTitle(props)}
        {renderRichText(props)}
        {renderPrice(props.item?.price!, t, priceStyle)}
        {props.isPageView || props.suppressCTA ? null : renderCTA(t)}
    </CardContent>
}

function renderCTA(t: TFunction) {
    return <CCOLinkText txt={t('c085_product_cta')} type={type.FOOTER}/>
}

function getCategoryTitle(item: SosDonationProduct): string {
    if (item.category && item.category.length > 0 && (item.category[0] as CmTaxonomy).value) {
        const tax = item.category[0] as CmTaxonomy;
        return tax.value!;
    } else {
        return item.title!;
    }
}

function renderTitle(props: Props) {
    return <React.Fragment>
        {props.suppressRoofline ? null : renderRoofline(props)}
        <CCOHeadlineAsText textAlign='left'
                           title={props.item.teaserTitle}
                           propertyName='properties.teaserTitle'
                           variant='h6'
                           sx={{marginBottom: '0.35em'}}/>
    </React.Fragment>;
}

function renderRoofline(props: Props) {
    const subline = getCategoryTitle(props.item);
    return <CCORoofline metadataId={props.item.id}
                        roofLine={subline}
                        roofLineCharLimit={ROOF_LINE_CHAR_LIMIT}
                        propertyName='properties.title'/>
}

function renderRichText(props: Props) {
    const propName = props.isPageView ? 'properties.detailText' : 'properties.teaserText';
    return <CCORichText metadataId={props.item.id}
                        textAsTree={props.suppressRoofline ? props.item.detailText?.textAsTree : props.item.teaserText?.textAsTree}
                        propertyName={propName}
                        charLimit={props.descriptionCharLimit ? props.descriptionCharLimit : DESCRIPTION_CHAR_LIMIT}/>;
}

/**
 * TeaserCardContent für C085ProductTeaser
 */
function ProductTeaserCardContent(props: Props) {
    const theme = useTheme();
    const color = theme.palette.primary.main;
    const {isWindowExtraLarge, lowerThanWindowMedium, lowerThanWindowLarge} = useWindowSize();
    const {t} = useTranslation();

    // Extrawurst für den Preis. Bei kleineren Brakpoints muss die Font-Size auch kleiner werden
    function getPriceStyle() {
        const normalStyle = {marginBottom: '0.35em'};
        const lowerThanMedStyle = {marginBottom: '0.35em', fontSize: '1.5rem!important'};
        const lowerThanLStyle = {marginBottom: '0.35em', fontSize: '1.75rem!important'};
        return  lowerThanWindowMedium ? lowerThanMedStyle : lowerThanWindowLarge ? lowerThanLStyle : normalStyle;
    }

    return <Box id={props.item.id} sx={productCardStyle(props, color)}>
        {
            renderContent(props, t, isWindowExtraLarge, getPriceStyle())
        }
    </Box>
}



export default ProductTeaserCardContent


